import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Slider } from '@mui/material';
import { SLIDER_SX, LAYOUT_CONSTANTS } from '../constants';
import PriceDisplay from './PriceDisplay';
import GetInTouchButton from './GetInTouchButton';

const PricingCard = ({
  title,
  subtitle,
  value,
  onChange,
  marks,
  price,
  totalPrice,
  currency,
  currencies,
  theme = 'light',
  isPopular = false,
  showCustom = false,
  showUnit = true,
}) => {
  const cardSx = {
    position: 'relative',
    ...(isPopular && {
      border: '2px solid #0f172a',
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    }),
  };

  const cardClasses = isPopular
    ? 'rounded-2xl bg-white p-6 md:p-8 transition-all duration-200 will-change-transform flex flex-col h-full items-center text-center'
    : 'rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-200 will-change-transform hover:-translate-y-0.5 flex flex-col h-full items-center text-center';

  return (
    <div className={cardClasses} style={cardSx}>
      {/* Most Popular badge */}
      {isPopular && (
        <Box
          sx={{
            position: 'absolute',
            top: -15,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#0f172a',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontSize: 12,
            fontWeight: 700,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}
        >
          Most Popular
        </Box>
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }} className="mb-1">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
        {subtitle}
      </Typography>

      <Slider
        aria-label={title}
        getAriaValueText={(value) => {
          const mark = marks.find((m) => m.value === value);
          return mark ? mark.longLabel || mark.label : `${value}`;
        }}
        value={value}
        onChange={onChange}
        min={1}
        max={marks.length}
        step={1}
        marks={marks}
        sx={SLIDER_SX}
        className="pricing-slider"
      />

      {/* Price Block */}
      <Box
        className="mt-6 flex flex-col gap-2"
        sx={{
          minHeight: LAYOUT_CONSTANTS.PRICE_BLOCK_MIN_HEIGHT,
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ visibility: price || !showCustom ? 'visible' : 'hidden' }}
        >
          Starting at
        </Typography>

        <PriceDisplay
          price={price}
          currency={currency}
          currencies={currencies}
          totalPrice={totalPrice}
          showCustom={showCustom}
          showTotal={true}
          showUnit={showUnit}
        />

        {/* Placeholder for height parity */}
        {showCustom && !price && (
          <Typography variant="body2" color="text.secondary" className="tabular-nums" sx={{ visibility: 'hidden' }}>
            Total: 0
          </Typography>
        )}
      </Box>

      {/* Push CTA to bottom consistently */}
      <div className="mt-auto" />

      {GetInTouchButton('large', {}, theme)}
    </div>
  );
};

PricingCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  marks: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string,
      longLabel: PropTypes.string,
    }),
  ).isRequired,
  price: PropTypes.number,
  totalPrice: PropTypes.number,
  currency: PropTypes.string.isRequired,
  currencies: PropTypes.object.isRequired,
  theme: PropTypes.string,
  isPopular: PropTypes.bool,
  showCustom: PropTypes.bool,
  showUnit: PropTypes.bool,
};

export default PricingCard;
